/**
 * P3-6 离职继承向导 4 步
 *   1. 预检 (preview API)
 *   2. 选策略 (all_to_one / by_rule / manual)
 *   3. 配置
 *   4. 预览 + 二次确认
 */
import { useState } from 'react';
import { Modal, Input, Radio, Select, message, Steps } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { Btn, Pill } from './ui';

type Strategy = 'all_to_one' | 'by_rule' | 'manual';

export function OffboardWizard({
  open, user, onClose, onDone,
}: {
  open: boolean;
  user: any;
  onClose: () => void;
  onDone: () => void;
}) {
  const [step, setStep] = useState(0);
  const [reason, setReason] = useState('');
  const [strategy, setStrategy] = useState<Strategy>('all_to_one');
  const [successorId, setSuccessorId] = useState<number | null>(null);
  const [rule, setRule] = useState<'by_stage' | 'by_region' | 'by_value'>('by_stage');
  const [manualMap, setManualMap] = useState<Record<number, number>>({});
  const [confirmName, setConfirmName] = useState('');

  const { data: preview } = useQuery<any>({
    queryKey: ['transfer-preview', user?.id, open],
    queryFn: () => $.get(`/admin/users/${user.id}/transfer-preview`),
    enabled: !!open && !!user?.id,
  });

  const submit = useMutation({
    mutationFn: () => {
      const body: any = { transfer_strategy: strategy, reason };
      if (strategy === 'all_to_one') body.successor_user_id = successorId;
      if (strategy === 'by_rule') body.rule = rule;
      if (strategy === 'manual') {
        body.customer_assignments = Object.entries(manualMap)
          .filter(([_, v]) => !!v)
          .map(([k, v]) => ({ customer_id: Number(k), to_user_id: Number(v) }));
      }
      return $.post(`/admin/users/${user.id}/transfer-and-deactivate`, body);
    },
    onSuccess: (r: any) => {
      message.success(`已转移 ${r.transferred} 位客户`);
      onDone();
    },
    onError: (e: any) => message.error(e?.message ?? '转移失败'),
  });

  const reset = () => {
    setStep(0); setReason(''); setStrategy('all_to_one');
    setSuccessorId(null); setRule('by_stage'); setManualMap({}); setConfirmName('');
  };

  const closeAll = () => { reset(); onClose(); };

  if (!user) return null;
  const successors = preview?.successors ?? [];
  const data = preview?.data ?? {};

  return (
    <Modal
      title={`离职继承向导 - ${user.realName}`}
      open={open}
      onCancel={closeAll}
      width={780}
      footer={null}
    >
      <Steps current={step} size="small" style={{ marginBottom: 24 }}
             items={[
               { title: '预检' }, { title: '选策略' },
               { title: '配置' }, { title: '确认' },
             ]} />

      {step === 0 && (
        <div>
          <div style={{ marginBottom: 12, fontSize: 13 }}>
            <strong>{user.realName}</strong> · {user.role}
            {user.teamId ? ` · 团队 #${user.teamId}` : ''}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
            <DataCard label="持客" v={data.customer_total} />
            <DataCard label="30 天跟进" v={data.followup_30d} />
            <DataCard label="合同" v={data.contract_count} />
            <DataCard label="订单" v={data.order_count} />
            <DataCard label="收款" v={data.payment_count} />
            <DataCard label="邮寄" v={data.shipment_count} />
            <DataCard label="资料" v={data.material_count} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>客户阶段分布</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 16 }}>
            {Object.entries(data.customer_by_stage ?? {}).map(([s, n]: any) => (
              <Pill key={s} tone="default">{s}: {n}</Pill>
            ))}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>
            AI 推荐继任者 Top {successors.length}
          </div>
          {successors.length === 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>暂无可推荐人选（团队为空或都已超负载）</div>
          )}
          {successors.map((s: any) => (
            <div key={s.user_id} style={{
              padding: 10, border: '1px solid var(--border)', borderRadius: 4, marginBottom: 6,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{s.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{s.reason}</div>
              </div>
              <Btn kind="default" onClick={() => {
                setStrategy('all_to_one'); setSuccessorId(s.user_id); setStep(2);
              }}>选此人</Btn>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Btn kind="default" onClick={closeAll}>取消</Btn>
            <div style={{ marginLeft: 'auto' }}><Btn kind="primary" onClick={() => setStep(1)}>下一步</Btn></div>
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          <Radio.Group value={strategy} onChange={(e) => setStrategy(e.target.value)}>
            <div style={{ marginBottom: 12 }}>
              <Radio value="all_to_one"><strong>A 全部转给一个继任者</strong>（推荐 · 最简单）</Radio>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 24 }}>
                所有客户一次性转给同一人，新 owner 接管所有未办待办 + SOP 状态
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Radio value="by_rule"><strong>B 按规则智能分配</strong></Radio>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 24 }}>
                按阶段 / 区域 / 价值在候选人之间分配
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <Radio value="manual"><strong>C 手动逐个分配</strong>（最灵活）</Radio>
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 24 }}>
                每个客户单独指定 owner
              </div>
            </div>
          </Radio.Group>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Btn kind="default" onClick={() => setStep(0)}>上一步</Btn>
            <div style={{ marginLeft: 'auto' }}><Btn kind="primary" onClick={() => setStep(2)}>下一步</Btn></div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          {strategy === 'all_to_one' && (
            <>
              <div style={{ fontSize: 12, marginBottom: 4 }}>选择继任者</div>
              <Select
                value={successorId ?? undefined}
                onChange={(v) => setSuccessorId(Number(v))}
                options={successors.map((s: any) => ({
                  value: s.user_id, label: `${s.name} · ${s.reason}`,
                }))}
                style={{ width: '100%' }}
                placeholder="选一个 AI 推荐的人选，或在'其他人选'里搜索"
              />
            </>
          )}
          {strategy === 'by_rule' && (
            <>
              <div style={{ fontSize: 12, marginBottom: 4 }}>分配规则</div>
              <Radio.Group value={rule} onChange={(e) => setRule(e.target.value)}>
                <Radio value="by_stage">按阶段轮询</Radio>
                <Radio value="by_value">高价值优先给 Top 1</Radio>
                <Radio value="by_region">按区域</Radio>
              </Radio.Group>
            </>
          )}
          {strategy === 'manual' && (
            <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
              💡 简化：本版手动分配模式暂仅支持"指定一个统一 owner"，等同于 all_to_one。
              批量手动分配 UI 后续迭代。
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, marginBottom: 4 }}>离职理由（必填）</div>
            <Input.TextArea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                            placeholder="如：主动辞职 / 试用期不通过 / 调岗 ..." />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Btn kind="default" onClick={() => setStep(1)}>上一步</Btn>
            <div style={{ marginLeft: 'auto' }}>
              <Btn kind="primary"
                   onClick={() => setStep(3)}
                   disabled={!reason || (strategy === 'all_to_one' && !successorId)}>
                下一步
              </Btn>
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <div style={{
            padding: 12, background: 'var(--warning-bg, #fff8e1)', borderRadius: 4, marginBottom: 16,
          }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>⚠️ 即将执行以下操作（不可逆）：</div>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
              <li>{user.realName} 账号停用（status=inactive），所有 token 失效</li>
              <li>名下 {data.customer_total} 位客户 owner_id 改为继任者</li>
              <li>未完成 daily_todos 跟着转移</li>
              <li>策略：<strong>{strategy}</strong>{strategy === 'all_to_one' ? `（继任者：#${successorId}）` : ''}{strategy === 'by_rule' ? `（规则：${rule}）` : ''}</li>
              <li>给每个继任者推钉钉通知</li>
            </ul>
          </div>
          <div style={{ fontSize: 12, marginBottom: 4 }}>
            为防误操作，请输入 <strong>{user.realName}</strong> 确认：
          </div>
          <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)}
                 placeholder={user.realName} />
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Btn kind="default" onClick={() => setStep(2)}>上一步</Btn>
            <div style={{ marginLeft: 'auto' }}>
              <Btn kind="primary"
                   onClick={() => submit.mutate()}
                   disabled={confirmName !== user.realName || submit.isPending}>
                {submit.isPending ? '执行中…' : '确认执行'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function DataCard({ label, v }: { label: string; v?: number }) {
  return (
    <div style={{
      padding: 10, background: 'var(--bg-2)', borderRadius: 4, textAlign: 'center',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 500 }}>{v ?? 0}</div>
    </div>
  );
}
